/**
 * Network Status Monitor
 * Detects online/offline status and notifies listeners
 */

class NetworkMonitor {
  constructor() {
    this.isOnline = navigator.onLine;
    this.listeners = new Set();
    this.reconnectListeners = new Set();
    
    // Setup event listeners
    this.init();
  }

  init() {
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
    
    // Periodic connectivity check (every 30 seconds)
    setInterval(() => this.checkConnectivity(), 30000);
    
    console.log('📡 Network monitor initialized');
  }

  handleOnline() {
    const wasOffline = !this.isOnline;
    this.isOnline = true;
    console.log('✅ Network: ONLINE');
    
    // Notify all listeners
    this.listeners.forEach(callback => callback(true));
    
    // Notify reconnect listeners if we were offline
    if (wasOffline) {
      console.log('🔄 Network reconnected - triggering resume uploads');
      this.reconnectListeners.forEach(callback => callback());
    }
  }

  handleOffline() {
    this.isOnline = false;
    console.log('❌ Network: OFFLINE');
    
    // Notify all listeners
    this.listeners.forEach(callback => callback(false));
  }

  async checkConnectivity() {
    // Try to fetch a small resource to verify connectivity
    try {
      const response = await fetch('/api/health', {
        method: 'HEAD',
        cache: 'no-cache',
        timeout: 5000
      });
      
      if (response.ok && !this.isOnline) {
        this.handleOnline();
      }
    } catch (error) {
      if (this.isOnline) {
        this.handleOffline();
      }
    }
  }

  /**
   * Subscribe to network status changes
   */
  subscribe(callback) {
    this.listeners.add(callback);
    
    // Call immediately with current status
    callback(this.isOnline);
    
    // Return unsubscribe function
    return () => this.listeners.delete(callback);
  }

  /**
   * Subscribe to reconnection events (online after offline)
   */
  onReconnect(callback) {
    this.reconnectListeners.add(callback);
    
    // Return unsubscribe function
    return () => this.reconnectListeners.delete(callback);
  }

  /**
   * Get current online status
   */
  getStatus() {
    return this.isOnline;
  }

  /**
   * Wait for online status
   */
  async waitForOnline(timeout = 60000) {
    if (this.isOnline) return true;

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Network timeout'));
      }, timeout);

      const unsubscribe = this.subscribe((isOnline) => {
        if (isOnline) {
          clearTimeout(timeoutId);
          unsubscribe();
          resolve(true);
        }
      });
    });
  }
}

// Export singleton instance
export default new NetworkMonitor();
